import { useState } from "react";
import { useSelector } from "react-redux";
import LazyLoad from "react-lazy-load";

import { Grid, Box, makeStyles } from "@material-ui/core";

import { PowerAssets } from "../../../buildings/tabs/properties/AllBuildingsPowerAssets";
import powerAssetRevenueImg from "../../../../assets/images/building/powerAssetRevenue.svg";
import { BuildingAlert } from "./buildingsMiantenanceAlerts";
import { TenancyDataInfo } from "./tenancyData/tenancyDataInfo";
import { TenancyDataGraph } from "./tenancyData/tenancyDataGraph";
import BellImg from "../../../../assets/images/icons/bell_black.svg";
import TenancyImg from "../../../../assets/images/icons/tenancy.svg";
import ProfitabilityImg from "../../../../assets/images/icons/profit.svg";
import RevenuesImg from "../../../../assets/images/icons/nav/revenues.svg";
import ElectricityImg from "../../../../assets/images/icons/electricity.svg";
import { IconTitle } from "../../../../components/parts";
import { Button } from "../../../../components/base";
import { ActionButtonsGroup } from "../../../../components/buttonsGroup/ActionButtonsGroup";
import {
  Unpaid,
  Meters,
  PriceModel,
  PublicAreaPowerCosts,
  PowerConsumptionCosts,
  PowerConsumptionUsageType,
  PowerConsumptionUsagePayments,
  PowerConsumptionOverTimeComponent,
  ConnectionStatusComponent,
  ComplexElectricityConnectionStatus,
} from "./electricity";
import {
  ProfitabilityComponent,
  GrossRentalRevenuesComponent,
  ProfitabilityOverTimeComponent,
  AvgUnrealizedPraasTradingRevenues,
} from "./buildingProfitability";
import { UnitsList } from "../units/unitsList";
import { UnitDetail } from "../units/unitDetail";
import { unitListFormatter } from "../units/unitListRow";
import { SupplyRevenuesTotal } from "./electricity/SupplyRevenuesTotal";
import { RevenueBreakdownOverTimeComponent } from "./electricity/RevenueBreakdownOverTimeComponent";
import { TenantsRebatePolicyModal } from "../../../../components/modals";

const useStyles = makeStyles((theme) => ({
  root: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
}));

export const PropertiesTab = ({ building, complex }) => {
  const classes = useStyles();
  const { user } = useSelector((state) => state.auth);
  const [showUnitList, setShowUnitList] = useState(false);
  const [unit, setUnit] = useState();
  const [isOpen, setOpen] = useState(false);

  if (unit && showUnitList) {
    return (
      <UnitDetail
        building={building}
        complex={complex}
        unit={unit}
        setUnit={setUnit}
        setShowUnitList={setShowUnitList}
      />
    );
  }

  if (showUnitList) {
    return (
      <UnitsList
        buildingId={building?.id}
        complexId={complex?.id}
        setShowUnitList={setShowUnitList}
        onSelected={setUnit}
        formatter={unitListFormatter}
      />
    );
  }

  return (
    <Box className={classes.root}>
      <Grid style={{ marginBottom: 28 }} container justifyContent="space-between">
        <IconTitle
          iconSrc={TenancyImg}
          title="TENANCY DATA & ANALYTICS"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Box style={{ marginLeft: "auto", display: "flex", height: "fit-content" }}>
          <Box style={{ marginRight: "14px" }}>
            <Button text="PUBLIC AREA" />
          </Box>
          <Button text="UNITS LIST" onClick={() => setShowUnitList(true)} />
          <ActionButtonsGroup hideIntegrateBtn />
        </Box>
      </Grid>
      <LazyLoad>
        <TenancyDataInfo buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>
      <LazyLoad>
        <TenancyDataGraph buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>

      <IconTitle
        iconSrc={ElectricityImg}
        iconBlockStyle={{ paddingTop: 5, paddingLeft: 5 }}
        title="ELECTRICITY DATA & ANALYTICS"
      />

      <Grid container spacing={3} style={{ minHeight: 384 }}>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <Meters buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
        {building && (
          <>
            <Grid item sm={4}>
              <LazyLoad height="100%">
                <ConnectionStatusComponent id={building.id} />
              </LazyLoad>
            </Grid>
            <Grid item sm={4}>
              <LazyLoad height="100%">
                <PriceModel id={building.id} />
              </LazyLoad>
            </Grid>
          </>
        )}
        {complex && !building ? (
          <Grid item sm={8}>
            <LazyLoad height="100%">
              <ComplexElectricityConnectionStatus complex={complex} />
            </LazyLoad>
          </Grid>
        ) : null}
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PowerConsumptionUsageType
              buildingId={building?.id}
              complexId={complex?.id}
              title={
                building
                  ? "Building Power Consumption - Usage type"
                  : "Complex Power Consumption - Usage type"
              }
            />
          </LazyLoad>
        </Grid>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PowerConsumptionUsagePayments
              buildingId={building?.id}
              complexId={complex?.id}
              title={
                building
                  ? "Building Power Consumption - Payments"
                  : "Complex Power Consumption - Payments"
              }
            />
          </LazyLoad>
        </Grid>
        <Grid item sm={4}>
          <LazyLoad height="100%">
            <PublicAreaPowerCosts buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
      </Grid>

      <LazyLoad>
        <Unpaid buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <LazyLoad height="100%">
            <PowerConsumptionOverTimeComponent
              buildingId={building?.id}
              complexId={complex?.id}
              title={
                building
                  ? "Building Power Consumption Over Time"
                  : "Complex Power Consumption Over Time"
              }
            />
          </LazyLoad>
        </Grid>
        <Grid item xs={8}>
          <LazyLoad height="100%">
            <PowerConsumptionCosts
              buildingId={building?.id}
              complexId={complex?.id}
              titleFirst={
                building
                  ? "Avg. Building Power Consumption Costs"
                  : "Avg. Complex Power Consumption Costs"
              }
            />
          </LazyLoad>
        </Grid>
      </Grid>

      <Grid style={{ paddingTop: 25 }} container justifyContent="space-between" alignItems="center">
        <IconTitle
          iconSrc={RevenuesImg}
          title="PRaaS Electricity Supply Revenues"
          rootStyle={{
            marginTop: 0,
            marginBottom: 0,
          }}
        />
        <Box style={{ marginLeft: "auto", height: "fit-content" }}>
          <Button text="TENANTS REBATE POLICY" onClick={() => setOpen(true)} />
        </Box>
        <TenantsRebatePolicyModal
          isOpen={isOpen}
          setOpen={() => setOpen(false)}
          building={building}
        />
      </Grid>
      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item lg={4}>
          <LazyLoad height="100%">
            <SupplyRevenuesTotal
              buildingId={building?.id}
              complexId={complex?.id}
              title={"Revenues - Total"}
            />
          </LazyLoad>
        </Grid>
        <Grid item lg={8}>
          <LazyLoad height="100%">
            <RevenueBreakdownOverTimeComponent buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
      </Grid>

      {user.is_demo ? (
        <Grid>
          <IconTitle iconSrc={powerAssetRevenueImg} title="POWER ASSETS DATA & ANALYTICS " />
          <LazyLoad>
            <PowerAssets buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
      ) : null}

      <LazyLoad>
        <IconTitle
          iconSrc={ProfitabilityImg}
          title={building ? "BUILDING PROFITABILITY" : "Complex Profitability"}
        />
      </LazyLoad>

      <LazyLoad>
        <ProfitabilityOverTimeComponent buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>

      <LazyLoad>
        <ProfitabilityComponent buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>

      <Grid container spacing={3} style={{ marginTop: 30, minHeight: 384 }}>
        <Grid item xs={4}>
          <LazyLoad height="100%">
            <GrossRentalRevenuesComponent buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
        <Grid item xs={8}>
          <LazyLoad height="100%">
            <AvgUnrealizedPraasTradingRevenues buildingId={building?.id} complexId={complex?.id} />
          </LazyLoad>
        </Grid>
      </Grid>

      <LazyLoad>
        <IconTitle iconSrc={BellImg} title={building ? "BUILDING ALERTS" : "Complex Alerts"} />
      </LazyLoad>

      <LazyLoad>
        <BuildingAlert buildingId={building?.id} complexId={complex?.id} />
      </LazyLoad>
    </Box>
  );
};
